import React, {useContext, useState} from 'react';
import {Button, Dialogue, Dropdown, DropdownItem, IconButton} from '@albert-io/atomic';
import {invalidatePartialInterest, genericMandarkRequest} from 'resources/mandark.resource';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import errorCodes from 'client/errorCodes';

import ConfirmActionModal from 'client/components/ConfirmActionModal/ConfirmActionModal.react';

interface RosterContextValue {
  classroom: {getId: () => string; getName: () => string};
  queries: {invitations: {done: () => Record<string, unknown>}};
  setIsReady: (isReady: boolean) => void;
}

interface ConfirmActionRenderProps {
  isRequestInflight: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

interface ReinviteError {
  response: {body: {errors: Array<{code: string}>}};
}

interface Props {
  invitation: any;
}

export default function InvitedStudentsActionsDropdown({invitation}: Props) {
  const {classroom, queries, setIsReady} = useContext(
    ManageRosterContext
  ) as unknown as RosterContextValue;
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showExceededHardCapModal, setShowExceededHardCapModal] = useState(false);

  const handleRemoveInvitation = () => invitation.delete();

  const handleSendInvitation = () => {
    const classroomId = classroom.getId();
    const teacherId = masqueradeStore.getUserIdByMasqueradeState();
    const email = invitation.getEmail();

    return genericMandarkRequest(
      'post',
      {
        resourcePath: ['json', 'classroom_invitations', 'reinvite']
      },
      {
        data: {
          attributes: {
            classroom_id: classroomId,
            issuer_id: teacherId,
            email
          }
        }
      }
    );
  };

  const handleSuccess = () => {
    invalidatePartialInterest(queries.invitations.done());
    setIsReady(false);
  };

  const handleCloseHardCapExceededModal = () => setShowExceededHardCapModal(false);

  return (
    <>
      <Dropdown
        trigger={<IconButton icon='ellipsis-v' label='menu' />}
        position='bottom-end'
        data-testid='invited-students-actions-dropdown'
      >
        <DropdownItem
          as='button'
          icon={['fal', 'envelope']}
          onClick={() => setShowResendModal((showResend) => !showResend)}
        >
          Re-send Invitation
        </DropdownItem>
        <DropdownItem
          as='button'
          color='red'
          icon={['fal', 'trash-alt']}
          iconColor='negative'
          onClick={() => setShowRemoveModal((showRemove) => !showRemove)}
        >
          Remove Invitation
        </DropdownItem>
      </Dropdown>
      {showRemoveModal && (
        <ConfirmActionModal
          align='center'
          handleClose={() => setShowRemoveModal(false)}
          makeRequest={handleRemoveInvitation}
          onResolve={handleSuccess}
        >
          {({isRequestInflight, onCancel, onConfirm}: ConfirmActionRenderProps) => (
            <>
              <Dialogue.Body align='center'>
                Are you sure you want to remove the invitation for
                <br />
                <strong>{invitation.getEmail()}</strong>?
              </Dialogue.Body>
              <Dialogue.BtnGroup align='center'>
                <Button color='secondary' disabled={isRequestInflight} onClick={onCancel}>
                  Cancel
                </Button>
                <Button color='negative' disabled={isRequestInflight} onClick={onConfirm}>
                  Remove
                </Button>
              </Dialogue.BtnGroup>
            </>
          )}
        </ConfirmActionModal>
      )}
      {showResendModal && (
        <ConfirmActionModal
          align='center'
          handleClose={() => setShowResendModal(false)}
          makeRequest={handleSendInvitation}
          onResolve={handleSuccess}
          onReject={(error: ReinviteError) => {
            const errorCode = error.response.body.errors[0].code;
            if (errorCode === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT) {
              setShowExceededHardCapModal(true);
              setShowResendModal(false);
            }
          }}
        >
          {({isRequestInflight, onCancel, onConfirm}: ConfirmActionRenderProps) => (
            <>
              <Dialogue.Body align='center'>
                A new invitation to
                <br />
                <strong>{classroom.getName()}</strong> will be sent to
                <br />
                <strong>{invitation.getEmail()}</strong>.
              </Dialogue.Body>
              <Dialogue.BtnGroup align='center'>
                <Button color='secondary' disabled={isRequestInflight} onClick={onCancel}>
                  Cancel
                </Button>
                <Button disabled={isRequestInflight} onClick={onConfirm}>
                  Send
                </Button>
              </Dialogue.BtnGroup>
            </>
          )}
        </ConfirmActionModal>
      )}
      {showExceededHardCapModal && (
        <ConfirmActionModal align='center' handleClose={handleCloseHardCapExceededModal}>
          {() => (
            <>
              <Dialogue.Body>
                <strong>
                  Could not could not resend invitation because your school&apos;s license has
                  reached it&apos;s seat limit.
                </strong>
              </Dialogue.Body>
              <Dialogue.BtnGroup>
                <Button color='secondary' onClick={handleCloseHardCapExceededModal}>
                  Close
                </Button>
              </Dialogue.BtnGroup>
            </>
          )}
        </ConfirmActionModal>
      )}
    </>
  );
}
